import { changeLanguage } from '@gripp/i18n';
import {
  Feature,
  Graphback,
  isFeatureEnabledForContext,
  useAuthentication,
  useCurrentAccount,
  useCurrentWorkspace,
  useFeatures,
  useSetDefaultPreferredLanguage,
  useSetDefaultTheme,
  useStorageKeys,
  useTheme,
} from '@gripp/shared-logic';
import * as Sentry from '@sentry/react';
import { FC, useEffect, useState } from 'react';
import { Navigate, Outlet, useMatches, useNavigate } from 'react-router-dom';
import { Layout } from '../../dashboard/components/layout';
import { isInternalPath } from '../../shared/isInternalPath';
import { usePathWithParams } from '../../shared/hooks/usePathWithParams';
import { getRouteHandleValue } from '../../shared/routeHandle';

export const ProtectedRoute: FC = () => {
  const authentication = useAuthentication();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    authentication?.isAuthenticated || false
  );
  const [isAuthorized, setIsAuthorized] = useState<boolean | undefined>(
    undefined
  );
  const matches = useMatches();
  const path = usePathWithParams();
  const { account } = useCurrentAccount();
  const currentWorkspace = useCurrentWorkspace();
  const { data: featuresData, isLoading: featuresLoading } = useFeatures();
  const navigate = useNavigate();
  const storageKeys = useStorageKeys();
  const setDefaultPreferredLanguage = useSetDefaultPreferredLanguage();
  const setDefaultTheme = useSetDefaultTheme();
  const { setTheme } = useTheme();

  if (authentication?.isAuthenticated && authentication.accessToken) {
    Graphback.initialize(authentication.accessToken);
  }

  useEffect(() => {
    if (!authentication || authentication.isLoading) return;
    if (authentication.isAuthenticated) {
      setIsAuthenticated(true);
      return;
    }

    if (authentication && !authentication.isAuthenticated) {
      localStorage.setItem(storageKeys.redirectPath, path);
      // A second, durable copy. `redirectPath` is deleted by
      // `onAccessTokenReceived` the moment a token arrives, which is before the
      // app has finished landing anywhere — and a brand-new account is routed
      // through /onboarding, which never reads it. Sharing a link with someone
      // who has no account yet is the common case, so losing the destination
      // there strands them on a workspace they did not ask for.
      localStorage.setItem(storageKeys.pendingDestination, path);
    }

    navigate('/login');
  }, [authentication, navigate, path, storageKeys.redirectPath]);

  useEffect(() => {
    if (!isAuthenticated || !account) return;

    const requiredPermission = getRouteHandleValue(
      matches,
      'requiredPermission'
    );
    const requiredFeature = getRouteHandleValue(matches, 'requiredFeature');

    if (!account.hasPermission(requiredPermission)) {
      setIsAuthorized(false);
      return;
    }

    if (!requiredFeature) {
      setIsAuthorized(true);
      return;
    }

    // A feature-gated route must wait for the features query to settle before
    // deciding — a flag reads as disabled while loading (see
    // gripp-workspace/AGENTS.md "Feature flags read as disabled while
    // loading"), so deciding early would flash-redirect an enabled workspace
    // to /forbidden on a direct-URL hit. Stay in the `undefined`
    // (Authorizing) state until the features query resolves, same as the
    // permission check above waiting on `account`.
    if (featuresLoading || !featuresData) {
      setIsAuthorized(undefined);
      return;
    }

    const features: Feature[] = featuresData.items ?? [];
    const feature = features.find((f) => f.name === requiredFeature);
    setIsAuthorized(
      isFeatureEnabledForContext(feature, currentWorkspace?.id, account.id)
    );
  }, [
    account,
    isAuthenticated,
    matches,
    featuresData,
    featuresLoading,
    currentWorkspace,
  ]);

  // Resumed here rather than on any one landing route, because which route
  // wins is not knowable: /onboarding hands off to '/', and Home then forwards
  // to /home or /activities as soon as the account resolves. This component
  // wraps all of them, so it sees the first authenticated render whichever way
  // the app went. Cleared before navigating, so a failed navigation cannot
  // loop, and skipped when already on the destination.
  useEffect(() => {
    if (!isAuthenticated || !account) return;

    const pending = localStorage.getItem(storageKeys.pendingDestination);
    if (!pending) return;
    localStorage.removeItem(storageKeys.pendingDestination);
    if (isInternalPath(pending) && pending !== path) {
      navigate(pending, { replace: true });
    }
  }, [isAuthenticated, account, navigate, path, storageKeys.pendingDestination]);

  useEffect(() => {
    const onInit = async () => {
      if (!account) return;
      //TODO: init

      if (account.preferences?.preferredLanguage) {
        await changeLanguage(account.preferences.preferredLanguage);
      } else {
        await setDefaultPreferredLanguage(account);
      }

      // Reconcile the appearance preference with the account (the account
      // wins when it has one; a pre-sign-in local choice is promoted
      // otherwise). Failure to sync must not block auth init.
      try {
        setTheme(await setDefaultTheme(account));
      } catch (error) {
        Sentry.captureException(error);
      }

      Sentry.setTag('account_id', account.id);
    };

    onInit();
  }, [account]);

  if (isAuthorized === undefined) {
    return <Authorizing />;
  }

  return isAuthorized ? <PrivateRouterLayout /> : <Navigate to="/forbidden" />;
};

const PrivateRouterLayout = () => (
  <Layout>
    <Outlet />
  </Layout>
);

const Authorizing = () => <h3>Authorizing...</h3>;
